/**
 * Suggestions Center — unified validation inbox for AI-generated proposals.
 *
 * Filters by type/score, list view with checkboxes, bulk accept/reject,
 * and a manual "rescan" trigger per clone.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sparkles, Folder, Check, X, Loader2, RefreshCw, ArrowLeft, Inbox } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  wa_group_link: { label: 'Groupe WhatsApp → Dossier', icon: '💬', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' },
  contact_dedupe: { label: 'Doublon contact', icon: '👥', color: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' },
  unknown_sender: { label: 'Expéditeur inconnu', icon: '🔒', color: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300' },
  email_to_contact: { label: 'Nouveau contact email', icon: '✉️', color: 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300' },
  recall_to_dossier: { label: 'Réunion → Dossier', icon: '📹', color: 'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300' },
};

const SCORE_BADGE = (score) => {
  if (score >= 95) return 'bg-emerald-600 text-white';
  if (score >= 85) return 'bg-emerald-500 text-white';
  if (score >= 75) return 'bg-amber-500 text-white';
  return 'bg-muted text-muted-foreground';
};

export default function SuggestionsPage() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending_total: 0, by_type_pending: {} });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [filterType, setFilterType] = useState('all');
  const [minScore, setMinScore] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [persons, setPersons] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: 'pending', min_score: minScore };
      if (filterType !== 'all') params.type = filterType;
      if (selectedPerson !== 'all') params.person_id = selectedPerson;
      const [listRes, countsRes] = await Promise.all([
        axios.get(`${API}/suggestions`, { ...auth, params }),
        axios.get(`${API}/suggestions/counts`, auth),
      ]);
      setItems(listRes.data?.items || []);
      setCounts(countsRes.data || { pending_total: 0, by_type_pending: {} });
      setSelected(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, minScore, selectedPerson]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    // Load clone list once for the per-clone filter / scan selector
    axios.get(`${API}/persons`, auth)
      .then(r => setPersons(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decideOne = async (s, action, alternativeId = null) => {
    setBusyId(s.id);
    try {
      const res = await axios.post(`${API}/suggestions/${s.id}/decide`, {
        action, alternative_id: alternativeId
      }, auth);
      if (action === 'accept' || action === 'alternative') {
        if (s.type === 'contact_dedupe') {
          toast.success(`Contacts fusionnés sur « ${s.proposed_action?.target_contact_name || '?'} »`);
        } else if (s.type === 'wa_group_link') {
          const r = res.data?.effector_result || {};
          const re = r.reassigned_memories || 0;
          toast.success(`Lié à « ${s.proposed_action?.dossier_title || 'dossier'} »${re ? ` · ${re} mémoires réattribuées` : ''}`);
        } else {
          toast.success('Suggestion acceptée');
        }
      } else {
        toast.success(s.type === 'contact_dedupe' ? 'Doublon ignoré' : 'Suggestion rejetée');
      }
      setItems(prev => prev.filter(x => x.id !== s.id));
      // Refresh badge counters after a decision
      axios.get(`${API}/suggestions/counts`, auth)
        .then(r => setCounts(r.data || counts))
        .catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBusyId(null);
    }
  };

  const bulkDecide = async (action) => {
    const ids = Array.from(selected);
    if (!ids.length) {
      toast.error('Aucun élément sélectionné');
      return;
    }
    try {
      const res = await axios.post(`${API}/suggestions/bulk-decide`, { ids, action }, auth);
      toast.success(`${res.data?.ok || 0} traitées${res.data?.failed ? ` · ${res.data.failed} échec(s)` : ''}`);
      setItems(prev => prev.filter(x => !selected.has(x.id)));
      setSelected(new Set());
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur bulk');
    }
  };

  const launchScan = async (type) => {
    if (selectedPerson === 'all') {
      toast.error('Choisis d\'abord un clone à scanner');
      return;
    }
    setScanning(true);
    try {
      let url = `${API}/suggestions/scan/whatsapp-groups`;
      let payload = { person_id: selectedPerson, min_score: 75 };
      if (type === 'wa-participants') {
        url = `${API}/suggestions/scan/whatsapp-participants`;
        payload = { person_id: selectedPerson, min_groups: 2 };
      }
      const res = await axios.post(url, payload, auth);
      if (type === 'wa-participants') {
        toast.success(`${res.data?.suggested || 0} contacts à créer (${res.data?.eligible || 0} candidats analysés)`);
      } else {
        toast.success(
          `${res.data?.suggested || 0} suggestions générées (${res.data?.auto_safe || 0} avec confiance ≥85%)`
        );
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de scan');
    } finally {
      setScanning(false);
    }
  };

  const toggleAll = (checked) => {
    setSelected(checked ? new Set(items.map(x => x.id)) : new Set());
  };
  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <div className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-indigo-600" />
            <h1 className="text-lg font-semibold">Centre de Validation</h1>
            <Badge variant="secondary" data-testid="pending-total-badge">
              {counts.pending_total} en attente
            </Badge>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-4">
        {/* Filters */}
        <Card>
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Type</span>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[230px]" data-testid="filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous types ({counts.pending_total})</SelectItem>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v.icon} {v.label}{counts.by_type_pending?.[k] ? ` (${counts.by_type_pending[k]})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Score min</span>
              <Select value={String(minScore)} onValueChange={v => setMinScore(Number(v))}>
                <SelectTrigger className="w-[140px]" data-testid="filter-score"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">≥ 0 %</SelectItem>
                  <SelectItem value="75">≥ 75 %</SelectItem>
                  <SelectItem value="85">≥ 85 % (auto-safe)</SelectItem>
                  <SelectItem value="95">≥ 95 % (excellent)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Clone</span>
              <Select value={selectedPerson} onValueChange={setSelectedPerson}>
                <SelectTrigger className="w-[200px]" data-testid="filter-person"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les clones</SelectItem>
                  {persons.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="outline" size="sm" onClick={() => launchScan('wa-groups')}
                disabled={scanning || selectedPerson === 'all'}
                data-testid="scan-wa-btn"
                title={selectedPerson === 'all' ? 'Choisis un clone d\'abord' : 'Scanner les groupes WhatsApp de ce clone'}
              >
                {scanning ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Scanner groupes WA
              </Button>
              <Button
                variant="outline" size="sm" onClick={() => launchScan('wa-participants')}
                disabled={scanning || selectedPerson === 'all'}
                data-testid="scan-wa-participants-btn"
                title={selectedPerson === 'all' ? 'Choisis un clone d\'abord' : 'Détecter les participants WA inconnus'}
              >
                {scanning ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Scanner participants WA
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bulk action bar (only visible with selection) */}
        {selected.size > 0 && (
          <Card className="border-indigo-300 bg-indigo-50 dark:bg-indigo-950/30 dark:border-indigo-800">
            <CardContent className="p-3 flex items-center gap-3">
              <span className="text-sm font-medium">{selected.size} sélectionné{selected.size > 1 ? 's' : ''}</span>
              <Button size="sm" onClick={() => bulkDecide('accept')} data-testid="bulk-accept">
                <Check className="w-4 h-4 mr-1" /> Tout accepter
              </Button>
              <Button size="sm" variant="outline" onClick={() => bulkDecide('reject')} data-testid="bulk-reject">
                <X className="w-4 h-4 mr-1" /> Tout rejeter
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Désélectionner</Button>
            </CardContent>
          </Card>
        )}

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
          </div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center text-muted-foreground">
              <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Aucune suggestion en attente avec ces filtres.</p>
              <p className="text-xs mt-2">Choisis un clone et lance "Scanner groupes WA" pour générer des suggestions.</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              {/* Header row */}
              <div className="flex items-center gap-3 p-3 border-b bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                <Checkbox
                  checked={selected.size === items.length && items.length > 0}
                  onCheckedChange={toggleAll}
                  data-testid="select-all"
                />
                <span>Tout sélectionner</span>
              </div>

              {items.map(s => {
                const typeInfo = TYPE_LABELS[s.type] || { label: s.type, icon: '❔', color: 'bg-muted' };
                const isWaLink = s.type === 'wa_group_link';
                const isDedupe = s.type === 'contact_dedupe';
                const isEmailContact = s.type === 'email_to_contact';
                const isRecall = s.type === 'recall_to_dossier';
                const isParticipant = s.type === 'participant_to_dossier';
                return (
                  <div
                    key={s.id}
                    className="flex items-start gap-3 p-3 border-b last:border-b-0 hover:bg-muted/30 transition"
                    data-testid={`suggestion-row-${s.id}`}
                  >
                    <Checkbox
                      checked={selected.has(s.id)}
                      onCheckedChange={() => toggleOne(s.id)}
                      data-testid={`select-${s.id}`}
                    />
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`text-[10px] ${typeInfo.color} border-0`}>
                          {typeInfo.icon} {typeInfo.label}
                        </Badge>
                        <Badge className={`text-[10px] ${SCORE_BADGE(s.ai_score)} border-0`}>
                          {s.ai_score}%
                        </Badge>
                        {s.proposed_action?.auto_safe && (
                          <Badge variant="outline" className="text-[10px] border-emerald-400 text-emerald-700 dark:text-emerald-300">
                            ✨ Auto-safe
                          </Badge>
                        )}
                      </div>

                      {isWaLink && (
                        <p className="text-sm">
                          <span className="text-muted-foreground">Groupe</span>{' '}
                          <span className="font-medium">« {s.source_ref?.group_name || '?'} »</span>
                          <span className="text-muted-foreground"> → </span>
                          <span className="font-medium text-indigo-700 dark:text-indigo-300 inline-flex items-center gap-1">
                            <Folder className="w-3 h-3" /> {s.proposed_action?.dossier_title || '?'}
                          </span>
                        </p>
                      )}

                      {isDedupe && (
                        <p className="text-sm">
                          <span className="text-muted-foreground">Fusionner</span>{' '}
                          <span className="font-medium">« {s.source_ref?.incoming_name || '?' }</span>
                          {(s.source_ref?.incoming_phone || s.source_ref?.incoming_email) && (
                            <span className="text-muted-foreground text-xs">
                              {' '}({[s.source_ref?.incoming_phone, s.source_ref?.incoming_email].filter(Boolean).join(' · ')})
                            </span>
                          )}
                          <span className="font-medium">{' »'}</span>
                          <span className="text-muted-foreground"> avec </span>
                          <span className="font-medium text-indigo-700 dark:text-indigo-300">
                            « {s.proposed_action?.target_contact_name || '?'} »
                          </span>
                        </p>
                      )}

                      {isEmailContact && (
                        <p className="text-sm">
                          <span className="text-muted-foreground">Créer contact pour</span>{' '}
                          <span className="font-medium">
                            {(s.source_ref?.sender_names || []).filter(Boolean)[0] || s.source_ref?.email}
                          </span>
                          <span className="text-muted-foreground text-xs">
                            {' '}· {s.source_ref?.email} · {s.source_ref?.message_count || 1} email
                            {(s.source_ref?.message_count || 1) > 1 ? 's' : ''}
                          </span>
                        </p>
                      )}

                      {isRecall && (
                        <p className="text-sm">
                          <span className="text-muted-foreground">Réunion</span>{' '}
                          <span className="font-medium">« {s.source_ref?.meeting_title || 'sans titre'} »</span>
                          <span className="text-muted-foreground"> → </span>
                          <span className="font-medium text-indigo-700 dark:text-indigo-300 inline-flex items-center gap-1">
                            <Folder className="w-3 h-3" /> {s.proposed_action?.dossier_title || '?'}
                          </span>
                          {s.source_ref?.speaker_names?.length > 0 && (
                            <span className="text-muted-foreground text-xs">
                              {' '}· participants : {s.source_ref.speaker_names.slice(0, 3).join(', ')}
                            </span>
                          )}
                        </p>
                      )}

                      {isParticipant && (
                        <p className="text-sm">
                          <span className="text-muted-foreground">Créer contact</span>{' '}
                          <span className="font-medium">
                            {s.source_ref?.name || s.source_ref?.phone}
                          </span>
                          <span className="text-muted-foreground text-xs">
                            {' '}· {s.source_ref?.phone} · vu dans {s.source_ref?.group_count || 1} groupe
                            {(s.source_ref?.group_count || 1) > 1 ? 's' : ''}
                          </span>
                        </p>
                      )}

                      {(isWaLink || isRecall) && s.alternatives?.length > 0 && (
                        <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                          <span>Alternatives :</span>
                          {s.alternatives.slice(0, 3).map(a => (
                            <button
                              key={a.dossier_id}
                              type="button"
                              onClick={() => decideOne(s, 'alternative', a.dossier_id)}
                              disabled={busyId === s.id}
                              className="px-2 py-0.5 rounded border hover:bg-accent text-xs disabled:opacity-50"
                              data-testid={`alt-${s.id}-${a.dossier_id}`}
                              title="Lier à cette alternative"
                            >
                              {a.dossier_title} ({a.score}%)
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => decideOne(s, 'accept')}
                        disabled={busyId === s.id}
                        data-testid={`accept-${s.id}`}
                      >
                        {busyId === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                        {isDedupe ? 'Fusionner' : (isEmailContact || isParticipant) ? 'Créer' : 'Accepter'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => decideOne(s, 'reject')}
                        disabled={busyId === s.id}
                        data-testid={`reject-${s.id}`}
                      >
                        <X className="w-3 h-3 mr-1" /> {isDedupe ? 'Ignorer' : 'Rejeter'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
